import { MedicalAppointmentFormData } from 'components/MedicalRequestForm/schema'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import SpecialtiesService from 'services/specialties'
import { useDebounce } from 'hooks/useDebounce'
import { Option } from '../../types/option'
import { SearchSelect } from '../SearchMultiSelect'

export const SpecialtySelect = () => {
  const allOptionsRef = React.useRef<Option[]>([])
  const [filteredOptions, setFilteredOptions] = React.useState<Option[]>([])
  const [searchQuery, setSearchQuery] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const { control, watch } = useFormContext<MedicalAppointmentFormData>()
  const selected = watch('specialty')

  const fetchSpecialties = React.useCallback(async (query: string, page: number, isSearch = false) => {
    if (isSearch) {
      if (query.length < 2) {
        setFilteredOptions(allOptionsRef.current)
        return { pagination: { total: allOptionsRef.current.length, page } }
      }

      setLoading(true)
      try {
        const result = await SpecialtiesService.listSpecialties(query)
        const searchOptions = result.specialties.map(({ id, professional_board, title }) => ({
          id,
          name: title,
          type: professional_board,
        }))
        setFilteredOptions(searchOptions)
        return { pagination: { total: searchOptions.length, page } }
      } catch (error) {
        console.error('Erro ao buscar especialidades:', error)
        const filtered = allOptionsRef.current.filter(option =>
          option.name.toLowerCase().includes(query.toLowerCase())
        )
        setFilteredOptions(filtered)
        return { pagination: { total: filtered.length, page } }
      } finally {
        setLoading(false)
      }
    }

    if (query && query.length >= 2) {
      setLoading(true)
      try {
        const result = await SpecialtiesService.listSpecialties(query)
        const searchOptions = result.specialties.map(({ id, professional_board, title }) => ({
          id,
          name: title,
          type: professional_board,
        }))

        setFilteredOptions(state => [...state, ...searchOptions])
        return { pagination: { total: searchOptions.length, page } }
      } catch (error) {
        console.error('Erro ao buscar especialidades:', error)
        return { pagination: { total: 0, page } }
      } finally {
        setLoading(false)
      }
    }

    const result = await SpecialtiesService.listSpecialties()
    const newOptions = result.specialties.map(({ id, professional_board, title }) => ({
      id,
      name: title,
      type: professional_board,
    }))

    allOptionsRef.current = newOptions
    setFilteredOptions(newOptions)
    return { pagination: { total: newOptions.length, page } }
  }, [])

  React.useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      fetchSpecialties(debouncedSearchQuery, 1, true)
    } else if (debouncedSearchQuery.length === 0) {
      fetchSpecialties('', 1, false)
    }
  }, [debouncedSearchQuery, fetchSpecialties])

  return (
    <Controller
      control={control}
      name="specialty"
      render={({ field, fieldState: { error }}) => (
        <SearchSelect
          id='specialty-select'
          {...field}
          options={filteredOptions}
          selectedOption={selected as Option[]}
          loadOptions={fetchSpecialties}
          onSearchChange={setSearchQuery}
          loading={loading}
          floatingLabel="Especialidade*"
          inputPlaceholder="Digite e/ou selecione para adicionar"
          emptyOptionsMessage="Nenhum resultado. Verifique sua digitação."
          hasError={Boolean(error?.message)}
          messageError={error?.message}
        />
      )}
    />
  )
}
